import {
	Chart as ChartJS,
	LinearScale,
	CategoryScale,
	BarElement,
	PointElement,
	LineElement,
	Legend,
	Tooltip,
	LineController,
	BarController,
} from "chart.js";
import { cssToken } from "../../utilities/cssToken";
import { htmlLegendPlugin } from "../dashboard/chartLegend";

/**
 * The one chart theme (ASB-4377, design §7.2). Registers Chart.js once and
 * builds every chart's options from the design tokens, read at render time
 * through cssToken(): the body font, grid and axis colours, the tooltip, and
 * the HTML legend plugin the Console's trend chart already uses, which is the
 * only legend the panel charts draw.
 *
 * Scoped, not global: the theme is applied per chart by the renderers in this
 * folder rather than written into ChartJS.defaults, because defaults are
 * process-wide and would restyle the Console trend and the propensity curve
 * (their move onto this theme is the separate ticket §7 names). Nothing
 * outside src/components/charts/ imports chart.js — the review bot's
 * design-system check warns on it.
 */
let registered = false;
export function registerCharts() {
	if (registered) return;
	ChartJS.register(
		LinearScale,
		CategoryScale,
		BarElement,
		PointElement,
		LineElement,
		Legend,
		Tooltip,
		LineController,
		BarController
	);
	registered = true;
}

export const legendPlugin = htmlLegendPlugin;

/** A length token ("0.75rem", "4px") as a number of pixels, or `fallback` without a DOM. */
export function tokenPx(name, fallback) {
	const raw = cssToken(name);
	if (!raw) return fallback;
	const n = parseFloat(raw);
	if (!Number.isFinite(n)) return fallback;
	if (raw.endsWith("rem") || raw.endsWith("em")) {
		let base = 16;
		try {
			base = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
		} catch (e) {
			// no DOM: the browser default
		}
		return n * base;
	}
	return n;
}

/** The resolved token values a chart paints with. Undefined entries fall back to Chart.js's own defaults. */
export function chartTheme() {
	return {
		font: cssToken("--as-font-body"),
		grid: cssToken("--as-chart-grid"),
		axis: cssToken("--as-chart-axis"),
		muted: cssToken("--as-chart-muted"),
		text: cssToken("--as-color-text"),
		white: cssToken("--as-color-white"),
		tooltipRadius: tokenPx("--as-radius-md", 4),
		tickSize: tokenPx("--as-text-xs", 12),
		bodySize: tokenPx("--as-text-sm", 14),
		pad: tokenPx("--as-space-3", 12),
	};
}

/**
 * The shared Chart.js options every renderer starts from. `legendId` is the
 * element the HTML legend renders into; `xTick`/`yTick` format tick values;
 * `tooltipTitle`/`tooltipLabel` format the tooltip from Chart.js's items;
 * `tooltipExternal(model)` sees the tooltip model on every change.
 */

/** The hovered tooltip as one line of text ("Tue, Oct 13, 2026 — Security block: 92.41% of flagged (25,225 records, 25.48% of checked)"), or "" when hidden. */
export function tooltipText(model) {
	if (!model || model.opacity === 0) return "";
	const title = (model.title ?? []).join(" ");
	const lines = (model.body ?? []).flatMap((b) => b.lines ?? []);
	return [title, ...lines].filter(Boolean).join(" — ");
}
export function baseOptions(
	theme,
	{
		legendId,
		xTick,
		yTick,
		tooltipTitle,
		tooltipLabel,
		tooltipExternal,
		stacked = false,
		xTitle,
		yTitle,
	}
) {
	const font = { family: theme.font, size: theme.tickSize };
	// A `callback: undefined` would replace Chart.js's default tick formatter
	// with nothing (ticks would read "undefined"), so it is only set when given.
	const withCallback = (ticks, callback) => (callback ? { ...ticks, callback } : ticks);
	return {
		responsive: true,
		maintainAspectRatio: false,
		animation: false,
		interaction: { mode: "index", intersect: false },
		layout: { padding: { top: theme.pad, right: theme.pad } },
		plugins: {
			legend: { display: false },
			htmlLegend: { containerID: legendId },
			tooltip: {
				enabled: true,
				backgroundColor: theme.text,
				titleColor: theme.white,
				bodyColor: theme.white,
				titleFont: { family: theme.font, size: theme.bodySize, weight: "bold" },
				bodyFont: { family: theme.font, size: theme.bodySize },
				padding: theme.pad,
				cornerRadius: theme.tooltipRadius,
				boxPadding: 4,
				// Called with the tooltip model on every hover change, beside the
				// canvas tooltip: the renderers mirror it into a hidden live region.
				external: tooltipExternal ? (ctx) => tooltipExternal(ctx.tooltip) : undefined,
				callbacks: {
					title: (items) => (tooltipTitle ? tooltipTitle(items) : items[0]?.label ?? ""),
					label: (ctx) =>
						tooltipLabel ? tooltipLabel(ctx) : `${ctx.dataset.label}: ${ctx.formattedValue}`,
				},
			},
		},
		scales: {
			x: {
				stacked,
				grid: { display: false },
				border: { color: theme.axis },
				title: xTitle
					? { display: true, text: xTitle, color: theme.muted, font }
					: { display: false },
				ticks: withCallback({ color: theme.muted, font, autoSkip: true, maxRotation: 0 }, xTick),
			},
			y: {
				stacked,
				beginAtZero: true,
				grid: { color: theme.grid },
				border: { display: false },
				title: yTitle
					? { display: true, text: yTitle, color: theme.muted, font }
					: { display: false },
				ticks: withCallback({ color: theme.muted, font, maxTicksLimit: 6 }, yTick),
			},
		},
	};
}

export * from "./formatters";
